import React from 'react';
import * as mui from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';

const TerminatedUsersWidget = ({ terminatedUsers, onClick }) => {
  const highRiskUsers = terminatedUsers.filter(u => u.RISK_LEVEL === 'HIGH').length;
  const mediumRiskUsers = terminatedUsers.filter(u => u.RISK_LEVEL === 'MEDIUM').length;
  const lowRiskUsers = terminatedUsers.filter(u => u.RISK_LEVEL === 'LOW').length;
  
  // Show max 5 recent users
  const recentTerminations = [...terminatedUsers]
    .sort((a, b) => {
      const dateA = a.TERMINATION_DATE ? new Date(a.TERMINATION_DATE) : new Date(0);
      const dateB = b.TERMINATION_DATE ? new Date(b.TERMINATION_DATE) : new Date(0);
      return dateB - dateA;
    })
    .slice(0, 5);
  
  return (
    <mui.Paper sx={{ p: 2, height: '100%' }}>
      <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PersonOffIcon sx={{ mr: 1, color: 'error.main' }} />
        <mui.Typography variant="h6">Terminated User Analysis</mui.Typography>
        <mui.Box sx={{ flexGrow: 1 }} />
        <mui.Button 
          size="small" 
          endIcon={<VisibilityIcon />} 
          onClick={onClick}
        >
          View All
        </mui.Button>
      </mui.Box>
      
      <mui.Divider sx={{ mb: 2 }} />
      
      <mui.Grid container spacing={2} sx={{ mb: 2 }}>
        <mui.Grid item xs={4}>
          <mui.Paper 
            elevation={0}
            sx={{ 
              p: 1, 
              textAlign: 'center',
              bgcolor: 'error.lighter',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.light'
            }}
          >
            <ErrorIcon color="error" />
            <mui.Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              {highRiskUsers}
            </mui.Typography>
            <mui.Typography variant="body2">High Risk</mui.Typography>
          </mui.Paper>
        </mui.Grid>
        <mui.Grid item xs={4}>
          <mui.Paper 
            elevation={0}
            sx={{ 
              p: 1, 
              textAlign: 'center',
              bgcolor: 'warning.lighter',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.light'
            }}
          >
            <WarningIcon color="warning" />
            <mui.Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
              {mediumRiskUsers}
            </mui.Typography>
            <mui.Typography variant="body2">Medium Risk</mui.Typography>
          </mui.Paper>
        </mui.Grid>
        <mui.Grid item xs={4}>
          <mui.Paper 
            elevation={0}
            sx={{ 
              p: 1, 
              textAlign: 'center',
              bgcolor: 'info.lighter',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light'
            }}
          >
            <InfoIcon color="info" />
            <mui.Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
              {lowRiskUsers}
            </mui.Typography>
            <mui.Typography variant="body2">Low Risk</mui.Typography>
          </mui.Paper>
        </mui.Grid>
      </mui.Grid>
      
      <mui.Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
        Recent Terminated Users
      </mui.Typography>
      
      {recentTerminations.length > 0 ? (
        <mui.List dense disablePadding>
          {recentTerminations.map((user, index) => (
            <mui.ListItem 
              key={index}
              disablePadding
              sx={{
                py: 0.5,
                borderBottom: index < recentTerminations.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <mui.ListItemIcon sx={{ minWidth: 36 }}>
                {user.RISK_LEVEL === 'HIGH' ? (
                  <ErrorIcon fontSize="small" color="error" />
                ) : user.RISK_LEVEL === 'MEDIUM' ? (
                  <WarningIcon fontSize="small" color="warning" />
                ) : (
                  <InfoIcon fontSize="small" color="info" />
                )}
              </mui.ListItemIcon>
              <mui.ListItemText
                primary={user.USERNAME || user.EMAIL}
                secondary={`Terminated: ${new Date(user.TERMINATION_DATE).toLocaleDateString()}`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <mui.Chip 
                label={`${user.DAYS_SINCE_TERMINATION} days`} 
                size="small" 
                color={user.DAYS_SINCE_TERMINATION > 30 ? "error" : "default"}
                variant="outlined"
              />
            </mui.ListItem>
          ))}
        </mui.List>
      ) : (
        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
          <mui.Typography variant="body2" color="text.secondary">
            No terminated users found
          </mui.Typography>
        </mui.Box>
      )}
    </mui.Paper>
  );
};

export default TerminatedUsersWidget; 